import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { CityDistrict } from '@/models/city-district.model';
import { getCityDistrict } from '@/services/city-district.service';
import CityDistrictForm from './city-district-form';
import CityDistrictActions from './city-district-actions';
import { City } from '@/models/city.model';
import { getCity } from '@/services/city.service';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="city-district-detail">City District Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="city-district-actions">Actions</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;
  const cityId = useLocation()?.state?.cityId;

  const [activeTab, setActiveTab] = useState('city-district-detail');
  const [formObject, setFormObject] = useState<CityDistrict|undefined>(undefined);
  const [city, setCity] = useState<City|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);

  useEffect(() => {
    if (formObject || cityId) {
      getCity(formObject?.cityId ?? cityId).then((result: BaseResponseModel<City>) => {
        if (result.success) {
          setCity(result.result);
        }
      }).catch((err) => {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching city: ${errorModel?.errorMessage}`);
      });
    }
  },[formObject, cityId]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<CityDistrict> = await getCityDistrict(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching city district: ${errorModel?.errorMessage}`);
        if(cityId){
          nav(`/cities/${cityId}`, {state: {tab: 'city-districts'}});
        }else {
          nav(`/cities`);
        }
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit City District: ${formObject.name}` : 'New City District'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/cities'> Cities</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={() => nav(`/cities/${formObject?.cityId ?? cityId}`, {state:{tab:'city-districts'}})}> Districts</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'city-district-detail' && <CityDistrictForm formObject={formObject} city={city} pageState={pageState}/>}
          {activeTab == 'city-district-actions' && <CityDistrictActions formObject={formObject} city={city} fetchData={fetchData}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
